// ============================================================================
// 道路・公園データ取得 / 自前ビルドPMTiles
// ----------------------------------------------------------------------------
// buildingTiles.tsと同じ理由(Overpass混雑からの脱却)で、道路・公園も自前
// ビルドしたPMTiles(z12単一ズーム、tippecanoeで間引きなし)から取得する。
// ビルド手順は WORKLOG.md 参照。
//
// 注意: osmium exportのarea判定がOverpassの単純な「wayの座標列=ring」という
// 解釈と完全には一致せず、公園(leisure=park等)の一部がPolygonではなく
// LineStringとして出力される（実測で概ね半々）。ここでは旧Overpass実装と
// 同様に「閉じているかどうかを問わず、座標列をそのままringとして扱う」
// 方針で吸収する。
// ============================================================================

import { VectorTile } from "@mapbox/vector-tile";
import { PbfReader } from "pbf";
import { distanceMeters } from "./geo";
import type { LngLat, OsmWay, PublicArea, RoadsAndParks } from "./types";
import {
  TILE_ZOOM,
  getPMTiles,
  localToLngLat,
  ringArea,
  tileBboxDistance,
  tilesForBbox,
} from "./pmtilesCommon";

/** Cloudflare R2(パブリックバケット、CORS設定済み)でホスティング。 */
const TILE_URL = "https://pub-aac031ce777c439bbb845a5887dd9b21.r2.dev/japan-roads-parks.pmtiles";

// buildingTiles.tsと同じ理由。都心などの高密度エリアで大量の道路・公園を
// 同期デコードするとタブがクラッシュしうるため、総フィーチャー数を頭打ちに
// する（道路・公園は表示用途のみで計算には使わないため、多少間引いても
// 実害は小さい）。上限に達したタイルの扱いはbuildingTiles.tsと同様に、
// 打ち上げ地点に近いタイル・フィーチャーを優先する(単純な走査順打ち切りは
// 打ち上げ地点周辺の欠落を招く。上限値の理由もbuildingTiles.ts参照)。
const MAX_DECODED_FEATURES = 300000;

function isPublicAccess(props: Record<string, unknown>): boolean {
  return props.access !== "private" && props.access !== "no";
}
function isPark(props: Record<string, unknown>): boolean {
  return (
    props.leisure === "park" || props.landuse === "recreation_ground" || props.leisure === "garden"
  );
}

/** ジオメトリ種別を問わず、面積最大のパーツを1本のリング(座標列)として返す。 */
function largestRing(
  parts: { x: number; y: number }[][],
  tx: number,
  ty: number,
  extent: number
): LngLat[] | null {
  const rings = parts
    .map((pts) => pts.map((pt): LngLat => localToLngLat(pt.x, pt.y, tx, ty, TILE_ZOOM, extent)))
    .filter((ring) => ring.length >= 2);
  if (rings.length === 0) return null;
  return rings.reduce((a, b) => (ringArea(b) > ringArea(a) ? b : a));
}

/**
 * bbox [south, west, north, east] にかかるz12タイルをすべて取得し、
 * roads_parksレイヤーを道路・公園に振り分ける。
 */
export async function fetchRoadsAndParksFromTiles(
  south: number,
  west: number,
  north: number,
  east: number
): Promise<RoadsAndParks> {
  const p = getPMTiles(TILE_URL);
  const center: LngLat = [(west + east) / 2, (south + north) / 2];
  const tileCoords = tilesForBbox(south, west, north, east, TILE_ZOOM).sort(
    (a, b) =>
      tileBboxDistance(center, a.tx, a.ty, TILE_ZOOM) -
      tileBboxDistance(center, b.tx, b.ty, TILE_ZOOM)
  );

  // タイル本体の取得(I/O)は並列に行い、デコード(CPU)だけを打ち上げ地点に
  // 近いタイルから順に処理する(buildingTiles.tsと同じ理由・同じ設計)。
  const tileBytes = await Promise.all(tileCoords.map(({ tx, ty }) => p.getZxy(TILE_ZOOM, tx, ty)));

  type Candidate =
    | { kind: "road"; ring: LngLat[]; dist: number }
    | { kind: "park"; ring: LngLat[]; dist: number };
  const candidates: Candidate[] = [];
  for (let t = 0; t < tileCoords.length; t++) {
    if (candidates.length >= MAX_DECODED_FEATURES) break;
    const res = tileBytes[t];
    if (!res) continue;
    const { tx, ty } = tileCoords[t];
    const tile = new VectorTile(new PbfReader(new Uint8Array(res.data)));
    const layer = tile.layers.roads_parks;
    if (!layer) continue;
    for (let i = 0; i < layer.length; i++) {
      const f = layer.feature(i);
      if (!isPublicAccess(f.properties)) continue;
      const ring = largestRing(f.loadGeometry(), tx, ty, f.extent);
      if (!ring) continue;
      const dist = distanceMeters(center, ring[0]);
      if (isPark(f.properties)) {
        candidates.push({ kind: "park", ring, dist });
      } else if (f.properties.highway) {
        candidates.push({ kind: "road", ring, dist });
      }
    }
  }
  candidates.sort((a, b) => a.dist - b.dist);

  const roads: OsmWay[] = [];
  const parks: PublicArea[] = [];
  let nextParkId = 1;
  for (const c of candidates.slice(0, MAX_DECODED_FEATURES)) {
    if (c.kind === "park") parks.push({ id: nextParkId++, ring: c.ring });
    else roads.push({ nodes: c.ring });
  }
  return { roads, parks };
}
